// Szolgáltatás-hozzárendelés MÁSOLÁSA (klónozás) — tiszta leképezés: a FORRÁS detail + a CÉL (hatókör/ügyfél/
// fiók/indulás) → a create-űrlap értékei. A wire-body innen a KÖZÖS `serviceAssignmentFormToCreateBody`-val
// készül, tehát a másolás PONTOSAN ugyanazt a szerződést küldi, mint a kézi felvitel: egy leképezés van, nincs
// mit elcsúsztatni, és a BFF minden falát (tulajdonos-státusz, idegen fiók, kártya-tulajdon, one_time→tracking,
// komplex Σ-ár) ugyanúgy megkapja.
//
// ── MIT JELENT ITT A „PLAN KLÓNOZÁSA" ────────────────────────────────────────────────────────────────────────
// A legacy CRM-ben a `service_plans.scope` ∈ {`catalog`, `assignment`}: MINDEN hozzárendelés a SAJÁT klón-
// planjére mutatott (`source_plan_id` → a katalógus-eredeti). Az Atlas ETL ezt SZÁNDÉKOSAN kisimította
// (`apps/etl/src/steps/service-plans.ts`): a katalógusba csak a `scope='catalog'` planek kerültek, a klón
// egyedi értékei a hozzárendelés `planSnapshot`-jába, az árai pedig a `service-plan-assignment-prices`-ba.
// Az Atlasban tehát „a hozzárendelés planje" = a SNAPSHOT + a SAJÁT ár-sorok, és a másolás ezeket viszi át:
//
//   snapshot-mező              honnan a másolaton              élesben eltér a katalógustól (2953 élő sor)
//   ─────────────────────────  ──────────────────────────────  ──────────────────────────────────────────
//   pricingType                a `pricing` override-ból  ✅       2
//   vatRate                    a `pricing` override-ból  ✅      30
//   usageSourceType            külön mező                ✅     309
//   invoiceItemName            külön mező                ✅      67
//   invoiceItemNote            külön mező                ✅    1694
//   billingType                a KATALÓGUS-planből                0
//   name (konstrukció-név)     a KATALÓGUS-planből  ⚠️           52
//   mainCategory/subCategory   a KATALÓGUS-planből  ⚠️          6/10
//
// A három ⚠️-s mezőt a create-szerződés nem tudja felülírni (a `buildSnapshot` a katalógusból veszi), és ez így
// HELYES: az eltérés élesben LEGACY-ELAVULÁS, nem szándékos egyedi név — pl. a snapshot „RoomSome havidíj"-t
// őriz, miközben a katalógusban a konstrukció azóta „RoomSome Booking engine". Egy ÚJ hozzárendelésre a MAI
// katalógus-név a helyes; a snapshot-név visszaírása az assignment-scope-os plan fogalmát építené vissza, amit
// az ETL kivezetett. A SZÁMLÁRA kerülő szöveg (`invoiceItemName`/`invoiceItemNote`) ettől függetlenül ÁTJÖN.
//
// Az ÁR-SOROK átvitele a `pricesToForm` → `buildPriceRows` körön megy (ugyanaz, amit a szerkesztő is használ).
// Élesben mérve hűséges: a 2005 áras root sor mind a dokumentált mátrixban van (flat→unit · tiered→passive+
// tier+overflow · base_tiered→base+tier+overflow · commission→minimum), és egyetlen soron sincs duplikált
// elsődleges ár-sor, amit a `find` elnyelne.
import { type ServiceAssignmentDetailSchema } from '@atlas/contracts';
import { type z } from 'zod';

import {
  allowedStatuses,
  serviceAssignmentToFormValues,
  type ServiceAssignmentFormValues,
  type ServiceScope,
} from './form';

type ServiceAssignmentDetail = z.infer<typeof ServiceAssignmentDetailSchema>;

/** A másolás CÉLJA — ennyit kér be a párbeszédablak, minden mást a forrás ad. */
export interface ServiceAssignmentCloneTarget {
  scope: ServiceScope;
  clientId: string;
  /** `account` hatókörnél a cél-fiók; `client` hatókörnél figyelmen kívül. */
  accountId: string;
  /**
   * `client` hatókörnél az ÉRINTETT FIÓKOK (`assignment-accounts` pivot); `account` hatókörnél figyelmen kívül.
   *
   * MIÉRT A HÍVÓTÓL JÖN, nem a forrásból származtatva: a mező a SZÁMLÁZÁST vezérli (használat-alapú
   * ügyfél-szintű sornál ezek a hotelek adódnak össze), az üres pivot pedig a generálóban „az ügyfél ÖSSZES
   * fiókja" jelentésű fallback. Egy másik ügyfélhez másolt sornál tehát a forrás kijelölése értelmezhetetlen,
   * a csendes üres viszont rosszul számlázna — a párbeszédablak ezért KÉRDEZI, és a kezdőértéket is ő adja
   * (azonos ügyfél → a forrás kijelölése; másik ügyfél → az új ügyfél összes fiókja).
   */
  accountIds: readonly string[];
  /**
   * A MÁSOLAT indulás-dátuma (ISO `YYYY-MM-DD`): számlázás-típusnál a `billingStartDate`, nyilvántartásnál a
   * `contractStartDate`. MIÉRT KÉRDEZZÜK: a forrás indulása a FORRÁS szerződéséé — egy évekkel korábbi dátummal
   * létrejövő új sor visszamenőleges számlázásra lenne esedékes. Alapérték a mai nap.
   */
  startDate: string;
  /** A CÉL tulajdonos (ügyfél VAGY fiók) státusza — a másolt státuszt ez szűkíti (a BFF is 422-zi). */
  ownerStatus: string | null;
  /**
   * A MÉRT AZONOSÍTÓK a célon (`accountId|integrationId` kulcsok) — használat-alapú tételnél kötelező.
   *
   * MIÉRT A HÍVÓTÓL JÖN: a kötés FIÓK-azonosítókra mutat, a másolat cél-fiókja pedig jellemzően MÁS, mint a
   * forrásé — a forrás kötése ott értelmetlen (és a BFF 422-zné). A párbeszédablak ezért KÉRDEZI, a
   * kezdőértéke a forrás kötéséből az, ami a célon is választható (`cloneUsageRefDefaults`).
   */
  usageRefs: readonly string[];
}

/**
 * A másolat mért-azonosító kezdőértéke: a forrás kötéséből CSAK ami a célon is választható (azonos fiókra
 * másolva tehát megmarad, más fiókra másolva üres — ott a felhasználó választ).
 */
export function cloneUsageRefDefaults(
  sourceKeys: readonly string[],
  targetOptionKeys: readonly string[],
): string[] {
  const valid = new Set(targetOptionKeys);
  return sourceKeys.filter((k) => valid.has(k));
}

/**
 * Melyik ÜGYFÉLHEZ KÖTÖTT hivatkozásokat kell elengedni a másolaton. Külön (exportált) függvény, mert a
 * párbeszédablak a FIGYELMEZTETŐ szöveghez is ezt kérdezi — a felhasználó MENTÉS ELŐTT lássa, mi nem jön át.
 *
 * `true` (= elengedjük), ha a cél-ügyfél eltér a forrásétól:
 *   • `paymentCardId` — a BFF 422-zi az idegen ügyfél kártyáját (`MSG_FOREIGN_PAYMENT_CARD`),
 *   • `contactId` (a szülőn ÉS a komponenseken) — a kapcsolattartó az ügyfél fiókjaihoz kötött partner-oldali
 *     személy; a választó is csak a cél-ügyfél kapcsolattartóit kínálja (`contactsForClient`). A BFF ezt NEM
 *     ellenőrzi, tehát itt a fal,
 * A SZAKÉRTŐ (`expertId`) MINDIG átjön: belső munkatárs, nem ügyfél-függő. Az ÉRINTETT FIÓKOK nem ezen a
 * szabályon múlnak: azokat a párbeszédablak kérdezi (l. `ServiceAssignmentCloneTarget.accountIds`).
 */
export function cloneDropsClientRefs(
  detail: Pick<ServiceAssignmentDetail, 'clientId'>,
  targetClientId: string,
): boolean {
  return targetClientId !== detail.clientId;
}

/**
 * A másolat ŰRLAP-ÉRTÉKEI. A `serviceId`-t és a komponensek `serviceId`-jét a hívó oldja fel a plan-katalógusból
 * (ugyanúgy, mint a szerkesztő) — a `pricing`/`components`/`invoiceItem*` a forrásból jön VÁLTOZATLANUL.
 *
 * Amit a másolat NEM örököl:
 *   • a komponensek `id`-ja → `''` (ÚJ gyerek; az id-vel küldött komponenst a szerver létező sor frissítésének
 *     venné, és az idegen id a cél-tenantban nem is létezik),
 *   • a ZÁRÁS-dátumok (`billingEndDate`/`contractEndDate`) → `''`: a forrás lezárása a forrás szerződéséé, egy
 *     már lejárt dátummal a másolat azonnal halott sorként születne,
 *   • az indulás-dátum → a CÉL-ból (lásd `ServiceAssignmentCloneTarget.startDate`),
 *   • ügyfél-váltásnál a kártya és a kapcsolattartó (lásd `cloneDropsClientRefs`).
 */
export function serviceAssignmentCloneValues(
  detail: ServiceAssignmentDetail,
  target: ServiceAssignmentCloneTarget,
  serviceId: string,
  planServiceMap?: (planId: string) => string,
): ServiceAssignmentFormValues {
  const base = serviceAssignmentToFormValues(detail, serviceId, planServiceMap);
  const elenged = cloneDropsClientRefs(detail, target.clientId);
  const isClient = target.scope === 'client';
  const isBilling = base.type === 'billing';
  // ESETI DÍJ: az indulás-dátum a „Számlázás dátuma" (kezdet = vég, szerződés-dátum nincs)
  const isOneTime = base.billingType === 'one_time';

  // A tulajdonos állapota szűkítheti a státuszt (függő ügyfél alá csak függő sor mehet). Ha a forrás státusza
  // nem megengedett a célnál, a LEGMAGASABB engedélyezettre esünk — ugyanaz a szabály, mint a create-formon.
  const engedett = allowedStatuses(target.ownerStatus);
  const status = engedett.includes(base.status) ? base.status : engedett[0]!;

  return {
    ...base,
    scope: target.scope,
    clientId: target.clientId,
    accountId: isClient ? '' : target.accountId,
    status,
    billingStartDate: isBilling || isOneTime ? target.startDate : '',
    billingEndDate: isOneTime ? target.startDate : '',
    contractStartDate: isBilling || isOneTime ? '' : target.startDate,
    contractEndDate: '',
    accountIds: isClient ? [...target.accountIds] : [],
    usageRefs: [...target.usageRefs],
    paymentCardId: elenged ? '' : base.paymentCardId,
    contactId: elenged ? '' : base.contactId,
    expertId: base.expertId,
    components: base.components.map((c) => ({
      ...c,
      id: '',
      contactId: elenged ? '' : c.contactId,
    })),
  };
}

/** Amit a másolás-párbeszéd ÖSSZEFOGLALÓJA kiír — a felhasználó mentés ELŐTT lássa, mi jön át. */
export interface ServiceAssignmentCloneSummary {
  /** A KATALÓGUS-konstrukció neve, amit a másolat kapni fog (nem feltétlenül a forrás snapshot-neve — l. fent). */
  planName: string | null;
  pricingType: string | null;
  /** A hozzárendelés saját ár-sorainak száma. */
  priceCount: number;
  /** A komponens-fa gyerekeinek száma (0 → egyszerű hozzárendelés). */
  componentCount: number;
  /** Van-e átvihető felelős (kapcsolattartó/szakértő) a szülőn vagy bármelyik komponensen. */
  hasPeople: boolean;
}

/** A forrás detail + a katalógus-plan neve → az összefoglaló. A plan-nevet a hívó a `refs`-ből adja. */
export function serviceAssignmentCloneSummary(
  detail: ServiceAssignmentDetail,
  catalogPlanName: string | null,
): ServiceAssignmentCloneSummary {
  return {
    planName: catalogPlanName ?? detail.planName,
    pricingType: detail.pricingType ?? detail.catalogPricingType,
    priceCount: detail.prices.length,
    componentCount: detail.children.length,
    hasPeople:
      detail.contactId !== null ||
      detail.expertId !== null ||
      detail.children.some((c) => c.contactId !== null || c.expertId !== null),
  };
}
